/**
 * Iter 163 · TaskViewSwitcher — tab bar + view renderer.
 *
 * Parent (`Tasks.jsx` or `ProjectDetail.jsx`) passes tasks + callbacks.
 * The switcher persists the chosen view in localStorage per `storageKey`.
 *
 * The "List" view is rendered by the parent (existing AdvancedTable)
 * because it needs access to editable columns; this switcher exposes
 * a `renderList` render-prop for that case.
 */
import { useEffect, useState, lazy, Suspense } from "react";
import { motion } from "framer-motion";
import {
  Kanban, Calendar, Activity, LayoutDashboard, GanttChart, List, ListOrdered,
  Repeat, Receipt, LayoutGrid,
} from "lucide-react";
// Feb 2026 R291b perf · The heavy views (dashboard / kanban / calendar /
// feed / timeline / cards) are code-split so the Tasks page's initial
// paint doesn't ship all six renderers. Each chunks on first render of
// its tab; subsequent switches are instant because the module is
// already warm.
const KanbanView = lazy(() => import("./TaskViews").then((m) => ({ default: m.KanbanView })));
const CalendarView = lazy(() => import("./TaskViews").then((m) => ({ default: m.CalendarView })));
const FeedView = lazy(() => import("./TaskViews").then((m) => ({ default: m.FeedView })));
const DashboardView = lazy(() => import("./TaskViews").then((m) => ({ default: m.DashboardView })));
const TimelineView = lazy(() => import("./TaskViews").then((m) => ({ default: m.TimelineView })));
const CardsView = lazy(() => import("./CardsView"));
const PowerfulGantt = lazy(() => import("./PowerfulGantt"));
const RecurringSeriesReport = lazy(() => import("./RecurringSeriesReport"));
const _ViewFallback = () => (
  <div className="text-xs text-muted-foreground italic py-6 text-center animate-pulse">
    Loading view…
  </div>
);
// (PowerfulGantt + RecurringSeriesReport are now lazy-loaded above.)


const VIEWS = [
  // R148d · Feb 2026 — Dashboard leads the sequence so PMs land on the
  // health summary first; List/Cards/Kanban/etc. remain grouped.
  { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { id: "list", label: "List", icon: List },
  { id: "cards", label: "Cards", icon: LayoutGrid },   // R138h · Feb 2026
  { id: "kanban", label: "Kanban", icon: Kanban },
  { id: "calendar", label: "Calendar", icon: Calendar },
  { id: "feed", label: "Feed", icon: Activity },
  { id: "gantt", label: "Gantt", icon: GanttChart },
  { id: "timeline", label: "Timeline", icon: ListOrdered },
  { id: "recurring", label: "Recurring", icon: Repeat },
  { id: "billing", label: "Billing", icon: Receipt },  // R69 · finance.view-gated
];

export default function TaskViewSwitcher({
  tasks, members, patchTask, onOpenTask,
  storageKey = "task-view",
  renderList,
  renderBilling,  // R69 · finance.view billing surface, provided by parent
  defaultView = "list",
  wsId, projectId, projects = [],
  selectedIds = [], setSelectedIds,
  onCreated,
  allowedViews,  // R33 · { list, kanban, calendar, feed, dashboard, gantt, timeline, recurring, billing }
  // Iter Feb2026 · Phase swim-lane toggle for the Kanban board.
  // Parent (ProjectTasksSubTab) passes both when the project has phases.
  // Feb 2026 R292w · Also supports "category", "assignee", "priority".
  kanbanGroupBy,      // "status" | "category" | "assignee" | "priority" | "phase"
  onKanbanGroupByChange,
  phases = [],
  // Feb 2026 R292w · Admin-managed task-category rows so the Kanban
  // category swimlanes resolve a label + colour per category.
  taskCategories = [],
}) {
  // R33 · Filter down to permitted views. If `allowedViews` isn't
  // supplied (legacy callers), show all.
  const visibleViews = allowedViews
    ? VIEWS.filter(v => allowedViews[v.id])
    : VIEWS;
  const [view, setView] = useState(() => {
    try {
      const stored = localStorage.getItem(storageKey) || defaultView;
      // Discard stored view if user no longer has access to it.
      if (allowedViews && !allowedViews[stored]) {
        return visibleViews[0]?.id || defaultView;
      }
      return stored;
    } catch { return defaultView; }
  });

  useEffect(() => {
    try { localStorage.setItem(storageKey, view); } catch { /* ignore */ }
  }, [view, storageKey]);

  return (
    <div className="space-y-1.5" data-testid={`task-view-switcher-${storageKey}`}>
      <div className="flex items-center gap-0.5 border-b border-border overflow-x-auto"
           data-testid="task-view-tabs">
        {visibleViews.map((v) => {
          const Icon = v.icon;
          const active = view === v.id;
          return (
            <button
              key={v.id}
              type="button"
              onClick={() => setView(v.id)}
              title={v.label}
              aria-label={v.label}
              className={`relative px-2.5 py-1.5 text-xs font-medium flex items-center justify-center whitespace-nowrap transition-colors ${
                active ? "text-primary" : "text-muted-foreground hover:text-foreground"
              }`}
              data-testid={`task-view-tab-${v.id}`}
            >
              <Icon className="h-4 w-4" />
              {active && (
                <motion.div layoutId={`task-view-underline-${storageKey}`}
                             className="absolute bottom-0 left-0 right-0 h-0.5"
                             style={{ background: "hsl(var(--primary))" }} />
              )}
            </button>
          );
        })}
      </div>

      <div>
        {/* Feb 2026 R291b perf · Suspense fallback for the lazy views. */}
        <Suspense fallback={<_ViewFallback />}>
        {view === "list" && (renderList ? renderList() : (
          <div className="text-sm text-muted-foreground italic text-center py-6">
            List view is provided by the parent.
          </div>
        ))}
        {view === "cards" && (
          <CardsView tasks={tasks} members={members} onOpenTask={onOpenTask}
                       projects={projects} patchTask={patchTask}
                       wsId={wsId} projectId={projectId} onCreated={onCreated} />
        )}
        {view === "kanban" && (
          <>
            {/* Iter Feb2026 · Group-by toggle for the Kanban swimlanes.
                Feb 2026 R292w · The toggle is now always rendered (even
                without phases) so admins can switch between Status,
                Category, Assignee and Priority swimlanes. Phase stays
                hidden until the project configures phases. */}
            {onKanbanGroupByChange && (
              <div className="flex items-center justify-end gap-1 -mt-2 mb-2 flex-wrap"
                   data-testid="kanban-swimlanes-toolbar">
                <span className="text-[10px] uppercase tracking-widest text-muted-foreground mr-1">
                  Swimlanes:
                </span>
                {[
                  { k: "status",   label: "Status" },
                  { k: "category", label: "Category" },
                  { k: "assignee", label: "Assignee" },
                  { k: "priority", label: "Priority" },
                  ...((phases && phases.length > 0) ? [{ k: "phase", label: "Phase" }] : []),
                ].map(({ k, label }) => (
                  <button
                    key={k}
                    type="button"
                    onClick={() => onKanbanGroupByChange(k)}
                    className={`h-6 px-2 text-[11px] border rounded-none ${
                      (kanbanGroupBy || "status") === k
                        ? "bg-foreground text-background border-foreground"
                        : "bg-background border-border text-muted-foreground hover:bg-muted"
                    }`}
                    data-testid={`kanban-swimlanes-${k}`}>
                    {label}
                  </button>
                ))}
              </div>
            )}
            <KanbanView tasks={tasks} members={members} patchTask={patchTask}
                         onOpenTask={onOpenTask} wsId={wsId} projectId={projectId}
                         projects={projects}
                         selectedIds={selectedIds} setSelectedIds={setSelectedIds}
                         onCreated={onCreated}
                         groupBy={kanbanGroupBy || "status"}
                         phases={phases}
                         categories={taskCategories || []} />
          </>
        )}
        {view === "calendar" && (
          <CalendarView tasks={tasks} members={members} onOpenTask={onOpenTask}
                          wsId={wsId} projectId={projectId} projects={projects}
                          onCreated={onCreated} />
        )}
        {view === "feed" && (
          <FeedView tasks={tasks} members={members} onOpenTask={onOpenTask}
                     wsId={wsId} projectId={projectId} projects={projects}
                     onCreated={onCreated} />
        )}
        {view === "dashboard" && (
          <DashboardView tasks={tasks} members={members} wsId={wsId}
                           projectId={projectId} projects={projects}
                           onCreated={onCreated} />
        )}
        {view === "gantt" && (
          <PowerfulGantt tasks={tasks} members={members} onOpenTask={onOpenTask}
                          patchTask={patchTask}
                          wsId={wsId} projectId={projectId} projects={projects}
                          onCreated={onCreated} />
        )}
        {view === "timeline" && (
          <TimelineView tasks={tasks} members={members} onOpenTask={onOpenTask}
                         wsId={wsId} projectId={projectId} projects={projects}
                         onCreated={onCreated} />
        )}
        {view === "recurring" && (
          <RecurringSeriesReport wsId={wsId} projectId={projectId} onOpenTask={onOpenTask} />
        )}
        {view === "billing" && (renderBilling ? renderBilling() : (
          <div className="text-sm text-muted-foreground italic text-center py-6">
            Billing view is provided by the parent.
          </div>
        ))}
        </Suspense>
      </div>
    </div>
  );
}
